<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useLocale, type SupportedLocale } from '@/composables/useLocale'
import { useSettingsStore } from '@/stores/settings'

interface Props {
  /**
   * Visual variant:
   * - 'default' for standalone use (header, navbar)
   * - 'embedded' for inline form integration
   * - 'sidebar' for dark sidebar background
   */
  variant?: 'default' | 'embedded' | 'sidebar'
  /**
   * Show the locale label text (hidden on mobile by default for 'default' variant)
   */
  showLabel?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  showLabel: true,
})

const variant = computed(() => props.variant)
const showLabel = computed(() => props.showLabel)
const isSidebar = computed(() => props.variant === 'sidebar')

const { locale, localeOptions } = useLocale()
const settingsStore = useSettingsStore()

const isOpen = ref(false)
const isLoading = ref(false)
const containerRef = ref<HTMLElement | null>(null)

const currentLocaleLabel = computed(() => {
  return locale.value.toUpperCase()
})

/**
 * Button classes based on variant
 */
const buttonClasses = computed(() => {
  const base = 'tw-flex tw-items-center tw-gap-1.5 tw-p-2 tw-rounded-lg tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer'

  if (isSidebar.value) {
    return `${base} tw-text-secondary-300 hover:tw-text-white hover:tw-bg-secondary-700`
  }

  return `${base} tw-text-surface-600 hover:tw-text-surface-900 hover:tw-bg-surface-100`
})

/**
 * Dropdown classes based on variant
 */
const dropdownClasses = computed(() => {
  const base = 'tw-absolute tw-w-36 tw-rounded-lg tw-shadow-lg tw-py-1 tw-z-50'

  if (isSidebar.value) {
    // Position above for sidebar (like user menu), with dark styling
    return `${base} tw-bottom-full tw-mb-2 tw-left-0 tw-bg-secondary-700 tw-border tw-border-secondary-600`
  }

  return `${base} tw-right-0 tw-mt-2 tw-bg-surface-0 tw-border tw-border-surface-200`
})

/**
 * Option classes based on variant
 */
const getOptionClasses = (isSelectedOption: boolean): string => {
  const base = 'tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-cursor-pointer'

  if (isSidebar.value) {
    const selected = isSelectedOption ? 'tw-bg-primary-600/30' : ''
    return `${base} tw-text-secondary-200 hover:tw-bg-secondary-600 ${selected}`
  }

  const selected = isSelectedOption ? 'tw-bg-primary-50 dark:tw-bg-primary-900/30' : ''
  return `${base} tw-text-surface-700 hover:tw-bg-surface-100 ${selected}`
}

/**
 * Option label classes based on variant
 */
const optionLabelClasses = computed(() => {
  if (isSidebar.value) {
    return 'tw-text-secondary-300'
  }
  return 'tw-text-surface-600'
})

function toggleDropdown(): void {
  isOpen.value = !isOpen.value
}

function closeDropdown(): void {
  isOpen.value = false
}

async function handleLocaleChange(newLocale: SupportedLocale): Promise<void> {
  if (newLocale === locale.value) {
    closeDropdown()
    return
  }

  isLoading.value = true
  try {
    // Use settings store to ensure locale is persisted for cross-subdomain navigation
    await settingsStore.setLocale(newLocale)
  } finally {
    isLoading.value = false
    closeDropdown()
  }
}

function handleClickOutside(event: MouseEvent): void {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    closeDropdown()
  }
}

function isSelected(optionValue: SupportedLocale): boolean {
  return optionValue === locale.value
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div ref="containerRef" class="tw-relative" data-testid="language-switcher">
    <!-- Toggle Button -->
    <button
      type="button"
      :class="[buttonClasses, { 'tw-opacity-50 tw-pointer-events-none': isLoading }]"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      aria-label="Select language"
      data-testid="language-switcher-button"
      @click.stop="toggleDropdown"
    >
      <!-- Globe icon -->
      <svg class="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
        />
      </svg>
      <!-- Locale label -->
      <span
        v-if="showLabel"
        class="tw-text-sm tw-font-medium"
        :class="{ 'tw-hidden sm:tw-inline': variant === 'default' }"
        data-testid="language-switcher-label"
      >
        {{ currentLocaleLabel }}
      </span>
      <!-- Chevron icon - points up for sidebar (dropdown opens above), down for others -->
      <svg
        v-if="!isSidebar"
        class="tw-w-4 tw-h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
      <svg
        v-else
        class="tw-w-4 tw-h-4 tw-transition-transform"
        :class="{ 'tw-rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <Transition
      enter-active-class="tw-transition tw-ease-out tw-duration-100"
      enter-from-class="tw-transform tw-opacity-0 tw-scale-95"
      enter-to-class="tw-transform tw-opacity-100 tw-scale-100"
      leave-active-class="tw-transition tw-ease-in tw-duration-75"
      leave-from-class="tw-transform tw-opacity-100 tw-scale-100"
      leave-to-class="tw-transform tw-opacity-0 tw-scale-95"
    >
      <div
        v-if="isOpen"
        :class="dropdownClasses"
        role="listbox"
        aria-label="Available languages"
        data-testid="language-switcher-dropdown"
      >
        <button
          v-for="option in localeOptions"
          :key="option.value"
          type="button"
          :class="getOptionClasses(isSelected(option.value))"
          role="option"
          :aria-selected="isSelected(option.value)"
          :data-testid="`language-option-${option.value}`"
          @click="handleLocaleChange(option.value)"
        >
          <span class="tw-font-medium">{{ option.value.toUpperCase() }}</span>
          <span :class="optionLabelClasses">{{ option.label }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Component uses Tailwind utility classes exclusively for consistency with Astro */
</style>
